import { useEffect, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

// Generic fullscreen modal with keyboard nav, reused for photo and video content.
// `items` is an array of { render: () => ReactNode, caption?: string }.
export default function Lightbox({ items, index, onClose, onNav }) {
  const active = index != null ? items[index] : null;
  const contentRef = useRef(null);
  // Re-runs every navigation — the caption is fresh DOM each time (it's
  // inside the key={index} child below), so the mask should replay each time.
  useReveal(contentRef, [index]);

  const handleKey = useCallback(
    (e) => {
      if (index == null) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onNav(-1);
      if (e.key === "ArrowRight") onNav(1);
    },
    [index, onClose, onNav],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  useEffect(() => {
    if (index == null) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [index]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          ref={contentRef}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full text-paper/80 transition hover:bg-white/10 hover:text-paper"
          >
            <X size={22} strokeWidth={1.5} />
          </button>

          {items.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous"
                onClick={(e) => {
                  e.stopPropagation();
                  onNav(-1);
                }}
                className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-paper/70 transition hover:bg-white/10 hover:text-paper md:left-5"
              >
                <ChevronLeft size={26} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                aria-label="Next"
                onClick={(e) => {
                  e.stopPropagation();
                  onNav(1);
                }}
                className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-paper/70 transition hover:bg-white/10 hover:text-paper md:right-5"
              >
                <ChevronRight size={26} strokeWidth={1.5} />
              </button>
            </>
          )}

          <motion.div
            key={index}
            className="relative flex max-h-[86vh] max-w-[92vw] flex-col items-center"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {active.render()}
            {active.caption && (
              <p
                data-mask
                className="mt-4 max-w-xl text-center font-serif text-lg text-paper/90"
              >
                {active.caption}
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
